import { useState } from 'react'
import { Box, Typography, IconButton, Tooltip, TextField } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import { FS_MD, FS_SM, ICON_SM } from '../common/theme'

export interface QuestionBadgeProps {
  // Current numbering string (e.g. "Q1.2"). Empty/undefined → show fallback.
  numbering?: string | null
  // Shown when numbering is empty - typically the question's ordinal index.
  fallback: string | number
  // Called when the user confirms an edit. Receives the new value
  // (trimmed). The caller is responsible for persisting (e.g. pmSaveQuestion).
  onCommit: (value: string) => void | Promise<void>
  // Visual variant:
  //   • `badge` (default) - large, prominent. Used in BidQuestion's toolbar.
  //     Explicit Save/Cancel buttons; ignores blur so the user can click
  //     the buttons.
  //   • `inline` - compact list-row pattern. Commits on blur or Enter,
  //     cancels on Escape. No buttons.
  variant?: 'badge' | 'inline'
  // Optional sx applied to the displayed Typography (not the input).
  sx?: any
  // data-testid prefix for the underlying input + display nodes.
  testIdPrefix?: string
  // Make the displayed value dimmed when there's no real numbering yet.
  // (Inline mode default = true; badge mode default = false.)
  dimWhenEmpty?: boolean
}

// Shared question-numbering display + inline editor. Click the number to
// edit; the component renders either a prominent "badge" or a compact
// inline cell depending on the `variant`.
export default function QuestionBadge({
  numbering, fallback, onCommit,
  variant = 'badge', sx, testIdPrefix = 'qbadge', dimWhenEmpty,
}: QuestionBadgeProps) {

  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(numbering ?? '')

  const startEdit = () => {
    setDraft(numbering ?? '')
    setEditing(true)
  }

  const commit = () => {
    setEditing(false)
    const trimmed = draft.trim()
    if (trimmed !== (numbering ?? '')) onCommit(trimmed)
  }

  const cancel = () => {
    setEditing(false)
    setDraft(numbering ?? '')
  }

  const hasNumbering = !!numbering
  const display = hasNumbering ? numbering : String(fallback)
  const dim = dimWhenEmpty ?? (variant === 'inline')

  // ── INLINE variant - compact, commit on blur ────────────────────────────
  if (variant === 'inline') {
    if (editing) {
      return (
        <TextField
          size="small"
          autoFocus
          value={draft}
          placeholder={String(fallback)}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            e.stopPropagation()
            if (e.key === 'Enter') { e.preventDefault(); commit() }
            if (e.key === 'Escape') { e.preventDefault(); cancel() }
          }}
          onBlur={commit}
          onClick={e => e.stopPropagation()}
          inputProps={{ 'data-testid': `${testIdPrefix}_input` }}
          sx={{ width: 80, '& .MuiInputBase-input': { py: 0.15, px: 0.5, fontSize: FS_MD, fontWeight: 700 } }}
        />
      )
    }
    return (
      <Tooltip title="Edit question number" disableInteractive>
        <Typography
          data-testid={testIdPrefix}
          onClick={(e) => { e.stopPropagation(); startEdit() }}
          sx={{
            fontSize: FS_MD, fontWeight: 700, lineHeight: 1,
            color: hasNumbering ? 'text.primary' : (dim ? 'text.disabled' : 'text.primary'),
            cursor: 'pointer', userSelect: 'none',
            px: 0.25,
            '&:hover': { color: 'primary.main' },
            ...(sx ?? {}),
          }}
        >
          {display}
        </Typography>
      </Tooltip>
    )
  }

  // ── BADGE variant - prominent, with Save / Cancel buttons ───────────────
  if (editing) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
        <TextField
          size="small"
          autoFocus
          value={draft}
          placeholder={String(fallback)}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') { e.preventDefault(); commit() }
            if (e.key === 'Escape') { e.preventDefault(); cancel() }
          }}
          inputProps={{ 'data-testid': `${testIdPrefix}_input` }}
          sx={{ width: 160, '& .MuiInputBase-input': { fontSize: FS_SM, py: 0.25, px: 0.5 } }}
        />
        <Tooltip title="Save" disableInteractive>
          <IconButton size="small" sx={{ p: 0.25, color: 'success.main' }} onMouseDown={e => { e.preventDefault(); commit() }}>
            <CheckIcon sx={{ fontSize: ICON_SM }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Cancel" disableInteractive>
          <IconButton size="small" sx={{ p: 0.25, color: 'error.main' }} onMouseDown={e => { e.preventDefault(); cancel() }}>
            <CloseIcon sx={{ fontSize: ICON_SM }} />
          </IconButton>
        </Tooltip>
      </Box>
    )
  }

  return (
    <Tooltip title="Edit question number" disableInteractive>
      <Box
        data-testid={testIdPrefix}
        onClick={startEdit}
        sx={{
          display: 'flex', alignItems: 'baseline', gap: '4px',
          cursor: 'pointer', userSelect: 'none',
          '&:hover': { color: 'primary.main' },
          ...(sx ?? {}),
        }}
      >
        <Typography sx={{
          fontSize: FS_MD, fontWeight: 700, lineHeight: 1,
          color: hasNumbering ? 'text.primary' : (dim ? 'text.disabled' : 'text.primary'),
        }}>
          {display}
        </Typography>
      </Box>
    </Tooltip>
  )
}
